import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  MagnifyingGlass,
  ArrowsClockwise,
  Calendar,
  Users,
  Trash,
  X,
} from "@phosphor-icons/react";
import ConfirmDialog from "./ConfirmDialog";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROGRAM_LABELS = {
  hospice_family_support: "Hospice Family Support",
  bereavement_support: "Bereavement Support",
  online_chapel: "Online Chapel",
};

const REGISTERING_FOR_LABELS = {
  myself: "Myself",
  myself_and_family: "Myself + family / caregiver",
  family_member: "A family member",
};

const CONTACT_LABELS = { email: "Email", phone: "Phone", text: "Text" };

const STATUS_OPTIONS = [
  { value: "new", label: "New", color: "bg-blue-100 text-blue-800" },
  { value: "contacted", label: "Contacted", color: "bg-amber-100 text-amber-800" },
  { value: "confirmed", label: "Confirmed", color: "bg-emerald-100 text-emerald-800" },
  { value: "attended", label: "Attended", color: "bg-primary/15 text-primary" },
  { value: "waitlist", label: "Waitlist", color: "bg-purple-100 text-purple-800" },
  { value: "closed", label: "Closed", color: "bg-ink-100 text-ink-600" },
];

const statusPill = (value) => {
  const opt = STATUS_OPTIONS.find((o) => o.value === value) || STATUS_OPTIONS[0];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${opt.color}`}
    >
      {opt.label}
    </span>
  );
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const ClassRegistrationsAdmin = ({ onDataChange }) => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [program, setProgram] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmDelete, setConfirmDelete] = useState(null); // {mode:'single'|'bulk', ids:string[]}
  const [deleting, setDeleting] = useState(false);

  const refreshStats = useCallback(() => {
    axios
      .get(`${API_BASE}/admin/class-registrations/stats`, { withCredentials: true })
      .then((r) => setStats(r.data))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (program !== "all") params.program = program;
      if (status !== "all") params.status = status;
      if (q.trim()) params.q = q.trim();
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API_BASE}/admin/class-registrations`, { params, withCredentials: true }),
        axios.get(`${API_BASE}/admin/class-registrations/stats`, { withCredentials: true }),
      ]);
      setItems(listRes.data || []);
      setStats(statsRes.data);
    } catch {
      toast.error("Could not load class registrations.");
    } finally {
      setLoading(false);
    }
  }, [program, status, q]);

  useEffect(() => {
    load();
  }, [program, status, load]);

  // Prune selection whenever the visible list changes so we never "silently"
  // hold ids the admin can no longer see.
  useEffect(() => {
    setSelectedIds((prev) => {
      const visible = new Set(items.map((i) => i.id));
      const next = new Set();
      prev.forEach((id) => visible.has(id) && next.add(id));
      return next;
    });
  }, [items]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    load();
  };

  const openDetail = (reg) => setSelected(reg);
  const closeDetail = () => setSelected(null);

  const toggleRow = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  const allVisibleSelected =
    items.length > 0 && items.every((i) => selectedIds.has(i.id));
  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      clearSelection();
    } else {
      setSelectedIds(new Set(items.map((i) => i.id)));
    }
  };

  const askDeleteSingle = (id) =>
    setConfirmDelete({ mode: "single", ids: [id] });
  const askDeleteBulk = () => {
    if (selectedIds.size === 0) return;
    setConfirmDelete({ mode: "bulk", ids: Array.from(selectedIds) });
  };
  const cancelDelete = () => setConfirmDelete(null);

  const doDelete = async () => {
    if (!confirmDelete) return;
    const { mode, ids } = confirmDelete;
    setDeleting(true);
    try {
      if (mode === "single") {
        await axios.delete(
          `${API_BASE}/admin/class-registrations/${ids[0]}`,
          { withCredentials: true }
        );
        setItems((prev) => prev.filter((r) => r.id !== ids[0]));
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(ids[0]);
          return next;
        });
        if (selected?.id === ids[0]) closeDetail();
        toast.success("Registration deleted");
      } else {
        const res = await axios.post(
          `${API_BASE}/admin/class-registrations/bulk-delete`,
          { ids },
          { withCredentials: true }
        );
        const removed = new Set(ids);
        setItems((prev) => prev.filter((r) => !removed.has(r.id)));
        clearSelection();
        const n = res.data?.deleted ?? ids.length;
        toast.success(`${n} registration${n === 1 ? "" : "s"} deleted`);
      }
      refreshStats();
      onDataChange?.();
      setConfirmDelete(null);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        toast.error("Session ended. Please sign in again.");
      } else if (err?.response?.status === 404) {
        toast.error("Record no longer exists. Refreshing list…");
        load();
        setConfirmDelete(null);
      } else {
        toast.error("Could not delete. Please try again.");
      }
    } finally {
      setDeleting(false);
    }
  };

  const updateSelected = async (patch) => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API_BASE}/admin/class-registrations/${selected.id}`,
        patch,
        { withCredentials: true }
      );
      setSelected(res.data);
      setItems((prev) => prev.map((r) => (r.id === res.data.id ? res.data : r)));
      refreshStats();
      onDataChange?.();
      toast.success("Updated.");
    } catch {
      toast.error("Could not save the change.");
    } finally {
      setSaving(false);
    }
  };

  const totals = useMemo(
    () => ({
      total: stats?.total || 0,
      new: stats?.new || 0,
      hospice: stats?.by_program?.hospice_family_support || 0,
      bereavement: stats?.by_program?.bereavement_support || 0,
    }),
    [stats]
  );

  return (
    <section data-testid="class-registrations-section" className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-medium text-ink-900">
            Online Class Registrations
          </h2>
          <p className="mt-0.5 text-xs text-ink-500">
            Registrations for the Hospice Family Support and Bereavement Support online programs.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="cr-refresh"
          className="inline-flex items-center gap-1.5 rounded-full border border-ink-200 bg-white px-3 py-1.5 text-xs font-semibold text-ink-700 hover:border-primary hover:text-primary"
        >
          <ArrowsClockwise size={12} weight="bold" />
          Refresh
        </button>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Users} label="Total registrations" value={totals.total} testid="cr-stat-total" />
        <StatCard icon={Calendar} label="New (awaiting contact)" value={totals.new} accent testid="cr-stat-new" />
        <StatCard icon={Users} label="Hospice Family Support" value={totals.hospice} testid="cr-stat-hospice" />
        <StatCard icon={Users} label="Bereavement Support" value={totals.bereavement} testid="cr-stat-bereavement" />
      </div>

      {/* Filters */}
      <div className="rounded-2xl border border-ink-100 bg-white p-3 shadow-sm flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-full border border-ink-200 bg-white p-1" data-testid="cr-filter-program">
          {[
            { v: "all", l: "All programs" },
            { v: "hospice_family_support", l: "Hospice" },
            { v: "bereavement_support", l: "Bereavement" },
            { v: "online_chapel", l: "Chapel" },
          ].map((p) => (
            <button
              key={p.v}
              type="button"
              onClick={() => setProgram(p.v)}
              data-testid={`cr-program-${p.v}`}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${
                program === p.v ? "bg-primary text-white shadow-sm" : "text-ink-600 hover:text-primary"
              }`}
            >
              {p.l}
            </button>
          ))}
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="cr-filter-status"
          className="rounded-full border border-ink-200 bg-white px-3 py-1.5 text-xs font-semibold text-ink-700 focus:border-primary outline-none"
        >
          <option value="all">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <form onSubmit={handleSearchSubmit} className="flex-1 min-w-[220px] flex items-center gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
            />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name or email…"
              data-testid="cr-search-input"
              className="w-full rounded-full border border-ink-200 pl-8 pr-3 py-1.5 text-xs focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
            />
          </div>
          <button
            type="submit"
            data-testid="cr-search-submit"
            className="rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-white hover:bg-primary-dark"
          >
            Search
          </button>
        </form>
      </div>

      {/* Bulk action bar (only when at least one row is selected) */}
      {selectedIds.size > 0 && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-red-200 bg-red-50/70 px-4 py-2.5"
          data-testid="cr-bulk-bar"
        >
          <div className="text-sm text-ink-800">
            <strong data-testid="cr-bulk-count">{selectedIds.size}</strong>{" "}
            selected
            <span className="text-ink-500">
              {" "}
              (of {items.length} shown)
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={clearSelection}
              data-testid="cr-bulk-clear"
              className="rounded-full border border-ink-200 bg-white px-3 py-1.5 text-xs font-semibold text-ink-700 hover:bg-ink-50"
            >
              Clear selection
            </button>
            <button
              type="button"
              onClick={askDeleteBulk}
              data-testid="cr-bulk-delete"
              className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700"
            >
              <Trash size={12} weight="bold" />
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl border border-ink-100 bg-white overflow-x-auto">
        <table className="min-w-full text-sm" data-testid="cr-table">
          <thead className="bg-softblue/40 text-ink-700">
            <tr>
              <th className="px-3 py-3 w-8">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                  aria-label="Select all visible registrations"
                  data-testid="cr-select-all"
                  className="h-4 w-4 accent-primary cursor-pointer"
                />
              </th>
              <Th>Date</Th>
              <Th>Name</Th>
              <Th>Program</Th>
              <Th>Email</Th>
              <Th>Phone</Th>
              <Th>Registering for</Th>
              <Th>Contact</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-ink-500 text-sm">
                  {loading ? "Loading..." : "No registrations match the current filters."}
                </td>
              </tr>
            ) : (
              items.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`cr-row-${r.id}`}
                  onClick={() => openDetail(r)}
                  className={`border-t border-ink-100 hover:bg-softblue/20 cursor-pointer ${
                    selectedIds.has(r.id) ? "bg-red-50/40" : ""
                  }`}
                >
                  <td
                    className="px-3 py-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(r.id)}
                      onChange={() => toggleRow(r.id)}
                      aria-label={`Select registration for ${r.first_name} ${r.last_name}`}
                      data-testid={`cr-select-${r.id}`}
                      className="h-4 w-4 accent-primary cursor-pointer"
                    />
                  </td>
                  <Td className="whitespace-nowrap text-ink-600 text-xs">
                    {fmtDate(r.created_at)}
                  </Td>
                  <Td className="font-medium text-ink-900">
                    {r.first_name} {r.last_name}
                  </Td>
                  <Td className="text-ink-700">
                    {PROGRAM_LABELS[r.program] || r.program}
                  </Td>
                  <Td className="text-ink-700 text-xs">{r.email}</Td>
                  <Td className="text-ink-700 text-xs">{r.phone || "—"}</Td>
                  <Td className="text-ink-700 text-xs">
                    {REGISTERING_FOR_LABELS[r.registering_for] || r.registering_for}
                  </Td>
                  <Td className="text-ink-700 text-xs">
                    {CONTACT_LABELS[r.preferred_contact] || r.preferred_contact}
                  </Td>
                  <Td>{statusPill(r.status)}</Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <DetailDrawer
          registration={selected}
          onClose={closeDetail}
          onUpdate={updateSelected}
          onDelete={() => askDeleteSingle(selected.id)}
          saving={saving}
        />
      )}

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title={
          confirmDelete?.mode === "bulk"
            ? `Delete ${confirmDelete.ids.length} selected registration${
                confirmDelete.ids.length === 1 ? "" : "s"
              }?`
            : "Delete this registration?"
        }
        description={
          confirmDelete?.mode === "bulk"
            ? "This will permanently remove the selected registrations and cannot be undone."
            : "This will permanently remove this registration. This action cannot be undone."
        }
        confirmLabel={
          confirmDelete?.mode === "bulk"
            ? `Delete ${confirmDelete.ids.length} registration${
                confirmDelete.ids.length === 1 ? "" : "s"
              }`
            : "Delete Registration"
        }
        busy={deleting}
        onConfirm={doDelete}
        onCancel={cancelDelete}
        testId="cr-confirm-delete"
      />
    </section>
  );
};

const StatCard = ({ icon: Icon, label, value, accent = false, testid }) => (
  <div
    data-testid={testid}
    className={`rounded-2xl border p-4 shadow-sm ${
      accent ? "border-primary/30 bg-primary/5" : "border-ink-100 bg-white"
    }`}
  >
    <div className="flex items-center gap-2 text-ink-500">
      <Icon size={14} weight="duotone" className="text-primary" />
      <span className="text-[10px] font-semibold uppercase tracking-[0.18em]">
        {label}
      </span>
    </div>
    <div className="mt-2 font-heading text-2xl text-ink-900 font-medium">{value}</div>
  </div>
);

const Th = ({ children }) => (
  <th className="text-left text-[10px] font-semibold uppercase tracking-[0.18em] px-4 py-3">
    {children}
  </th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 ${className}`}>{children}</td>
);

const DetailDrawer = ({ registration, onClose, onUpdate, onDelete, saving }) => {
  const [notes, setNotes] = useState(registration.internal_notes || "");
  useEffect(() => {
    setNotes(registration.internal_notes || "");
  }, [registration.id, registration.internal_notes]);

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      role="dialog"
      aria-modal="true"
      data-testid="cr-detail-drawer"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-ink-900/40 backdrop-blur-sm"
      />
      <div className="relative w-full max-w-md h-full bg-white shadow-2xl flex flex-col">
        <div className="flex items-start gap-3 border-b border-ink-100 bg-gradient-to-br from-primary to-primary-dark px-5 py-4 text-white">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-secondary">
              Registration
            </div>
            <div className="mt-0.5 font-heading text-lg font-medium">
              {registration.first_name} {registration.last_name}
            </div>
            <div className="mt-0.5 text-xs opacity-90">{registration.program_label}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="cr-detail-close"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-white/85 hover:bg-white/10"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
          <FieldRow label="Registered">{fmtDate(registration.created_at)}</FieldRow>
          <FieldRow label="Email">
            <a href={`mailto:${registration.email}`} className="text-primary hover:underline">
              {registration.email}
            </a>
          </FieldRow>
          <FieldRow label="Phone">{registration.phone || "—"}</FieldRow>
          <FieldRow label="Registering for">
            {REGISTERING_FOR_LABELS[registration.registering_for] || registration.registering_for}
          </FieldRow>
          <FieldRow label="Preferred contact">
            {CONTACT_LABELS[registration.preferred_contact] || registration.preferred_contact}
          </FieldRow>
          <FieldRow label="Consent">{registration.consent ? "Given" : "No"}</FieldRow>
          {registration.message && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
                Message
              </div>
              <div className="mt-1 rounded-lg bg-softblue/40 border border-ink-100 px-3 py-2 text-sm text-ink-800 whitespace-pre-wrap">
                {registration.message}
              </div>
            </div>
          )}

          <div>
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
                Status
              </span>
              <select
                value={registration.status}
                onChange={(e) => onUpdate({ status: e.target.value })}
                disabled={saving}
                data-testid="cr-detail-status"
                className="mt-1 block w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div>
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
                Internal notes
              </span>
              <textarea
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="cr-detail-notes"
                className="mt-1 block w-full resize-none rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
              />
            </label>
            <button
              type="button"
              onClick={() => onUpdate({ internal_notes: notes })}
              disabled={saving}
              data-testid="cr-detail-save-notes"
              className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-white hover:bg-primary-dark disabled:opacity-60"
            >
              Save notes
            </button>
          </div>

          <div className="mt-4 border-t border-ink-100 pt-4">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
              Danger zone
            </div>
            <button
              type="button"
              onClick={onDelete}
              disabled={saving}
              data-testid="cr-detail-delete"
              className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-red-300 bg-white px-4 py-2 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60"
            >
              <Trash size={12} weight="bold" />
              Delete registration
            </button>
            <p className="mt-2 text-[11px] leading-snug text-ink-500">
              Use this only for test data, duplicates, spam, or clearly unwanted submissions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const FieldRow = ({ label, children }) => (
  <div>
    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
      {label}
    </div>
    <div className="mt-0.5 text-sm text-ink-800">{children}</div>
  </div>
);

export default ClassRegistrationsAdmin;
